import AuthSignInSignOut from '@/components/auth/Auth';
import SearchFormDialog from '@/components/route/dialogs/SearchFormDialog';
import HeaderMobileMenu from '@/components/ui/HeaderMobileMenu';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { SearchForm } from '@/components/ui/SearchForm';
import { event } from '@/lib/google-analytics';
import { dateLocalizedText } from '@/lib/utils';
import { ArrowLongRightIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { format, parseISO, startOfToday } from 'date-fns';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';

export default function RouteHeader({
	origin,
	destination,
	date,
	languageAlternates,
	navigation,
	showAuthDialog = false,
	className,
}) {
	const router = useRouter();
	const [isSearchDialogOpen, setSearchDialogOpen] = useState(false);
	const routeDate = date ? parseISO(date) : startOfToday();

	const handleLocationsClick = () => {
		setSearchDialogOpen(true);
		event('Search_Form_Modal', {
			category: router.pathname,
			label: 'Opened',
		});
	};

	const trackNavigation = (name) => {
		event('route_header_navigation', {
			category: router.pathname,
			label: name,
		});
	};

	return (
		<>
			<header
				id="route-header"
				className={clsx(
					'fixed inset-x-0 top-0 z-40 bg-white shadow-md lg:border-b lg:border-gray-200 lg:shadow-none',
					className
				)}
			>
				<div className="mx-auto max-w-7xl px-4 pt-2 sm:px-6 lg:px-8">
					<div className="shadow-xs flex items-start gap-x-2 rounded-full bg-white px-4 py-1.5 ring-1 ring-gray-300 lg:items-center lg:gap-x-4 lg:rounded-none lg:px-0 lg:shadow-none lg:ring-0">
						<HeaderMobileMenu>
							<Image
								priority
								src={'/icons/busbuster/apple-touch-icon.png'}
								width={32}
								height={32}
								alt="BusBuster header icon"
								className="h-8 w-8 shrink-0"
							/>
							<ChevronDownIcon className="h-4 w-4 text-gray-900" />
						</HeaderMobileMenu>
						<SearchForm
							className="hidden lg:flex xl:max-w-[750px]"
							switchLocationsEnabled
							compact
							initialSearch={{
								origin,
								destination,
								date: format(routeDate, 'yyyy-MM-dd'),
							}}
						/>
						<div className="flex grow flex-col justify-center overflow-hidden lg:hidden">
							<button
								onClick={handleLocationsClick}
								className="flex items-center gap-x-1 text-left text-sm font-semibold leading-tight text-gray-900"
							>
								<span className="max-w-[50%] truncate">{origin.shortName}</span>
								<ArrowLongRightIcon className="h-5 w-5 text-gray-700" />
								<span className="max-w-[50%] truncate">
									{destination.shortName}
								</span>
							</button>
							<span className="text-xs capitalize text-gray-500">
								{dateLocalizedText({ date: routeDate, locale: router.locale })}
							</span>
						</div>
						<div className="-mr-1 ml-auto inline-flex shrink-0 items-center gap-x-2">
							<LanguageSwitcher
								className="hidden lg:inline-block"
								languageAlternates={languageAlternates}
							/>
							<AuthSignInSignOut showAuthDialog={showAuthDialog} />
						</div>
					</div>
					{navigation && navigation.length > 0 && (
						<nav className="pb-1 pt-2 lg:ml-16">
							<div className="scrollbar-hide flex items-end justify-start gap-x-3 overflow-x-auto">
								{navigation.map((nav) => (
									<a
										onClick={() => trackNavigation(nav.name)}
										href={nav.href}
										key={nav.href}
										className="inline-flex shrink-0 border-b-2 border-white text-sm font-semibold text-gray-700 hover:border-gray-700 lg:text-base"
									>
										{nav.name}
									</a>
								))}
							</div>
						</nav>
					)}
				</div>
			</header>
			<SearchFormDialog
				isOpen={isSearchDialogOpen}
				onClose={() => setSearchDialogOpen(false)}
				initialSearch={{
					origin,
					destination,
					date: format(routeDate, 'yyyy-MM-dd'),
				}}
			/>
		</>
	);
}
